import { useMutation, useQuery } from '@tanstack/react-query';

import { AuthQueryKeys, useAuthApi, useRefreshAuthApi } from '@/api';
import { mapExchangeToken, mapRefreshToken } from '@/api/api-mappers/auth-mapper.ts';
import { globalEnums } from '@/shared/duck';

export const useExchangeToken = (code: string, redirectUri: string) => {
  const { authApi } = useAuthApi();
  const { data, isLoading, error } = useQuery({
    queryKey: [AuthQueryKeys.exchangeCodeOnToken, code],
    queryFn: async () => {
      const response = await authApi.exchangeTokensApiAuthExchangeTokensGet(code, redirectUri);
      return mapExchangeToken(response.data);
    },
    enabled: !!code,
  });

  return {
    exchangeTokenData: data,
    isExchangeLoading: isLoading,
    exchangeError: error,
  };
};

export const useRefreshToken = () => {
  const { refreshAuthApi } = useRefreshAuthApi();

  const { mutateAsync, isPending, error, data } = useMutation({
    mutationKey: [AuthQueryKeys.refreshToken],
    mutationFn: async () => {
      const response = await refreshAuthApi.refreshTokensApiAuthRefreshPost();
      return mapRefreshToken(response.data);
    },
  });

  return {
    refreshToken: mutateAsync,
    isRefreshing: isPending,
    refreshError: error,
    refreshedTokenData: data,
  };
};

export const useGetRoles = (accessToken?: string) => {
  const { authApi } = useAuthApi();

  const { data, isLoading } = useQuery({
    queryKey: [AuthQueryKeys.getRoles, accessToken],
    queryFn: async () => {
      const response = await authApi.refreshTokensApiAuthRolesGet(accessToken);
      return response.data;
    },
    enabled: !!accessToken,
  });

  return {
    roles: data?.roles as globalEnums.ERoles[],
    isRolesLoading: isLoading,
  };
};